/**
 * "Role in <team>": a member's role in their team, answered like an attribute.
 * In the attributes dropdown it sits directly under the Teams list, once per
 * team the member has picked that has set-up roles, and offers only that team's
 * roles plus "My role isn't listed". The backend keeps the answer as the
 * same record a manager sets in Operations, and enrols the member into the
 * role's programs (userMemberAttributes, teamRoleService).
 *
 * No React here, so it can be tested on its own (teamRoles.test.ts).
 */

/** The profile's `teamRoles` (GET /getUserProfile): set-up roles, and the member's own answers. */
export interface TeamRolesView {
  /** The org's Teams list. */
  teamsListId: string;
  /** Every set-up role (capabilities and levels confirmed), with its team. */
  roles: Array<{ id: string; name: string; teamValueId: string }>;
  /** The roles the member holds now, whoever gave them. */
  mine: Array<{ teamValueId: string; roleId: string; roleName?: string }>;
  /** Teams the member answered "My role isn't listed" for. */
  declinedTeams: string[];
  /** The set-up roles of their teams when they last answered. */
  ackRoleIds?: string[];
}

/** Per team: a role id, null for "None of these", absent while unanswered. */
export type RoleAnswers = Record<string, string | null>;

/** A team the member has picked that they can choose a role in. */
export interface RoleTeam {
  teamValueId: string;
  teamName: string;
  /** Its set-up roles, and the member's own role when it is not one of them (still theirs until they change it). */
  roles: Array<{ id: string; name: string }>;
}

interface AttributeList {
  _id: string;
  name: string;
  values: Array<{ _id: string; value: string }>;
}

/** The label of a team's role question. */
export const roleQuestion = (team: RoleTeam): string => `Role in ${team.teamName}`;

/** The teams the member has picked that have roles to choose from, in the Teams list's order. */
export function roleTeams(
  view: TeamRolesView | null | undefined,
  lists: AttributeList[],
  selectedValueIds: string[]
): RoleTeam[] {
  if (!view) return [];
  const list = lists.find((l) => l._id === view.teamsListId);
  if (!list) return [];
  const picked = new Set(selectedValueIds);
  return list.values
    .filter((v) => picked.has(v._id))
    .map((v) => {
      const roles = view.roles.filter((r) => r.teamValueId === v._id).map((r) => ({ id: r.id, name: r.name }));
      const held = view.mine.find((m) => m.teamValueId === v._id);
      if (held && !roles.some((r) => r.id === held.roleId))
        roles.push({ id: held.roleId, name: held.roleName || 'Your current role' });
      return { teamValueId: v._id, teamName: v.value, roles };
    })
    .filter((team) => view.roles.some((r) => r.teamValueId === team.teamValueId));
}

/**
 * The member's answers as they stand: the role they hold in a team, or "None of
 * these" for a team they declined. A role set up after they declined asks them
 * again, so that team starts unanswered.
 */
export function initialRoleAnswers(view: TeamRolesView | null | undefined): RoleAnswers {
  const answers: RoleAnswers = {};
  if (!view) return answers;
  const seen = new Set(view.ackRoleIds || []);
  for (const team of view.declinedTeams) {
    const newRole = view.roles.some((r) => r.teamValueId === team && !seen.has(r.id));
    if (!newRole) answers[team] = null;
  }
  for (const held of view.mine) answers[held.teamValueId] = held.roleId;
  return answers;
}

/** The teams still waiting for an answer. */
export const unansweredRoleTeams = (teams: RoleTeam[], answers: RoleAnswers): string[] =>
  teams.filter((t) => answers[t.teamValueId] === undefined).map((t) => t.teamValueId);

/** What the save sends (`teamRoles` on PUT /users/me/memberAttributes): an answer per team still picked. */
export const roleAnswersPayload = (
  teams: RoleTeam[],
  answers: RoleAnswers
): Array<{ teamValueId: string; roleId: string | null }> =>
  teams
    .filter((t) => answers[t.teamValueId] !== undefined)
    .map((t) => ({ teamValueId: t.teamValueId, roleId: answers[t.teamValueId] as string | null }));

/**
 * The view once the member's answers are saved, as the backend now holds it:
 * their role or "None of these" per team they answered, nothing for a team they
 * left, and the roles they saw as acknowledged. Applied straight after the
 * save, so the required prompt does not ask again before the profile is read.
 */
export function viewAfterAnswers(
  view: TeamRolesView,
  selectedValueIds: string[],
  teams: RoleTeam[],
  answers: RoleAnswers
): TeamRolesView {
  const picked = new Set(selectedValueIds);
  const mine = view.mine.filter((m) => picked.has(m.teamValueId));
  const declined = new Set(view.declinedTeams.filter((t) => picked.has(t)));
  const sent = roleAnswersPayload(teams, answers);
  for (const { teamValueId, roleId } of sent) {
    const at = mine.findIndex((m) => m.teamValueId === teamValueId);
    if (at >= 0) mine.splice(at, 1);
    if (roleId === null) declined.add(teamValueId);
    else {
      declined.delete(teamValueId);
      const roleName = teams.find((t) => t.teamValueId === teamValueId)?.roles.find((r) => r.id === roleId)?.name;
      mine.push({ teamValueId, roleId, roleName });
    }
  }
  return {
    ...view,
    mine,
    declinedTeams: Array.from(declined),
    ackRoleIds: sent.length ? view.roles.filter((r) => picked.has(r.teamValueId)).map((r) => r.id) : view.ackRoleIds
  };
}

/** What a member with no role is told, on their profile and in the left navigation. */
export const NO_ROLE_MESSAGE =
  'You have not yet selected a role. If your role does not appear, ask your Manager to add it to Dosen now.';

/**
 * Who the member is, for the no-role mark (GET /v3/admin/access, useNoRoleViewer):
 * an org admin (a platform admin is one) is never marked, and a team's managers
 * are never marked for that team. Nobody above them is there to add their role.
 */
export interface NoRoleViewer {
  orgAdmin: boolean;
  /** The teams they manage (Teams list value ids). */
  managedTeamIds: string[];
}

/**
 * The teams the member is in (values of the Teams list they picked) that have
 * roles set up and in which they hold none: they answered "My role isn't
 * listed", or haven't answered yet. Every program is for roles, so until they
 * hold one they are in none of that team's role programs. A team with no roles
 * set up has none to choose from, so it is never one of these. Nor is a team
 * they manage, and an org admin has none (`viewer`; without it, a member who
 * is neither). The profile marks these teams' pills, and the left navigation
 * marks the member when there is any (hasNoRole), so the two always agree.
 */
export function teamsWithoutRole(
  view: TeamRolesView | null | undefined,
  lists: AttributeList[],
  selectedValueIds: string[],
  viewer?: NoRoleViewer | null
): Array<{ id: string; name: string }> {
  if (!view || viewer?.orgAdmin) return [];
  const teams = lists.find((l) => l._id === view.teamsListId);
  if (!teams) return [];
  const picked = new Set(selectedValueIds);
  const managed = new Set(viewer?.managedTeamIds ?? []);
  return teams.values
    .filter(
      (v) =>
        picked.has(v._id) &&
        !managed.has(v._id) &&
        view.roles.some((r) => r.teamValueId === v._id) &&
        !view.mine.some((m) => m.teamValueId === v._id)
    )
    .map((v) => ({ id: v._id, name: v.value }));
}

/**
 * The member is flagged as having no role: in at least one of their teams with roles set up, they hold none.
 * Never an org admin, and never for a team they manage (`viewer`, as teamsWithoutRole).
 */
export const hasNoRole = (
  view: TeamRolesView | null | undefined,
  lists: AttributeList[],
  selectedValueIds: string[],
  viewer?: NoRoleViewer | null
): boolean => teamsWithoutRole(view, lists, selectedValueIds, viewer).length > 0;

/**
 * What to do about the questions still unanswered: an attribute list takes a
 * value or "None of these apply to me"; a team's role question a role or "My
 * role isn't listed". `lists` and `roles` count the ones of each still owed.
 */
export function unansweredAdvice(lists: number, roles: number): string {
  if (roles === 0) return 'Pick a value or tick "None of these apply to me".';
  if (lists === 0) return 'Pick a role or tick "My role isn\'t listed".';
  return 'Pick a value or tick "None of these apply to me", and for a team\'s role, pick a role or tick "My role isn\'t listed".';
}

/** One team's answer in words, for a chip or a profile row; null while unanswered. */
export function roleAnswerLabel(team: RoleTeam, answers: RoleAnswers): string | null {
  const answer = answers[team.teamValueId];
  if (answer === undefined) return null;
  if (answer === null) return 'None';
  return team.roles.find((r) => r.id === answer)?.name ?? null;
}
